
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Home, 
  Briefcase, 
  Users, 
  MessageSquare, 
  Calendar, 
  Settings, 
  Menu, 
  X,
  ChevronRight,
  FileText,
  BarChart
} from "lucide-react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";

type SidebarLink = {
  name: string;
  href: string;
  icon: React.ElementType;
  tier?: 1 | 2 | 3;
};

interface SidebarProps {
  userTier?: 1 | 2 | 3;
  onLogout: () => void;
}

export function Sidebar({ userTier = 1, onLogout }: SidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  const links: SidebarLink[] = [
    { name: "Dashboard", href: "/dashboard", icon: Home },
    { name: "Jobs", href: "/jobs", icon: Briefcase },
    { name: "Drivers", href: "/drivers", icon: Users },
    { name: "Messages", href: "/messages", icon: MessageSquare, tier: 2 },
    { name: "Calendar", href: "/calendar", icon: Calendar, tier: 2 },
    { name: "Reports", href: "/reports", icon: BarChart, tier: 3 },
    { name: "Documents", href: "/documents", icon: FileText, tier: 3 },
    { name: "Settings", href: "/settings", icon: Settings }
  ];

  // Filter links based on user tier
  const filteredLinks = links.filter(link => !link.tier || link.tier <= userTier);

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-20 p-2 rounded-md bg-white dark:bg-gray-800 shadow-md"
        aria-label="Open menu"
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-30 bg-black bg-opacity-50"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full z-40 
          w-64 bg-white dark:bg-gray-900 shadow-xl
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar header */}
          <div className="px-4 py-5 flex items-center justify-between border-b border-gray-200 dark:border-gray-800">
            <Logo />
            <div className="flex items-center space-x-2">
              <ThemeToggle />
              <button
                onClick={() => setIsOpen(false)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Navigation links */}
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            <ul className="space-y-1">
              {filteredLinks.map((link) => {
                const isActive = location.pathname.startsWith(link.href);
                return (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className={`
                        flex items-center justify-between px-3 py-2 rounded-md transition-colors
                        ${isActive 
                          ? "bg-primary text-white" 
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }
                      `}
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="flex items-center">
                        <link.icon className="h-5 w-5 mr-3" />
                        <span>{link.name}</span>
                      </div>
                      {link.tier && link.tier > 1 && (
                        <span className={`tier-badge ${link.tier === 2 ? "tier-2" : "tier-3"}`}>
                          Tier {link.tier}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Sidebar footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={onLogout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ChevronRight className="h-5 w-5 mr-3" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Content offset for desktop */}
      <div className="lg:pl-64 transition-all duration-300">
        {/* Your content goes here */}
      </div>
    </>
  );
}
